import { useAuth0 } from '@auth0/auth0-react';
import type { DriverRosterBlockDto, RosterDriverDto } from '@my/directory-api';
import { useRoster } from '@my/directory-api/hooks';
import { cn, PageHeader } from '@my/ui';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useMemo, useState } from 'react';
import { getCurrentOrganisationId } from '@/auth/currentOrg';
import { AddBlockPanel, type BlockMode } from './components/AddBlockPanel';
import { DriversGrid } from './components/DriversGrid';
import { RosterContextMenu } from './components/RosterContextMenu';
import { RosterGrid } from './components/RosterGrid';
import { LEGEND, monthInfoFor, STATUS } from './lib/mockData';

type Tab = 'roster' | 'generate' | 'drivers';

const TABS: { id: Tab; label: string }[] = [
  { id: 'roster', label: 'Roster' },
  { id: 'generate', label: 'Generate' },
  { id: 'drivers', label: 'Drivers' },
];

/**
 * Driver Roster. The Drivers tab is wired to live data (driver configs + add /
 * edit); the monthly Roster grid + Generate are still prototype (mock data) —
 * wired in a later pass.
 */
export default function RosterPage() {
  const { user } = useAuth0();
  const orgId = useMemo(() => getCurrentOrganisationId(user), [user]);
  const [tab, setTab] = useState<Tab>('roster');
  const [menu, setMenu] = useState<{
    driverId: string;
    x: number;
    y: number;
    day?: number;
  } | null>(null);
  const [addBlock, setAddBlock] = useState<{
    driverId: string;
    mode: BlockMode;
    date: string;
    block?: DriverRosterBlockDto;
  } | null>(null);
  const [cursor, setCursor] = useState({ year: 2026, month: 5 }); // June 2026 (0-based month)
  const month = useMemo(() => monthInfoFor(cursor.year, cursor.month), [cursor]);
  const stepMonth = (delta: number) =>
    setCursor((c) => {
      const m = c.month + delta;
      if (m < 0) return { year: c.year - 1, month: 11 };
      if (m > 11) return { year: c.year + 1, month: 0 };
      return { year: c.year, month: m };
    });

  // BE month param is 1-based; `cursor.month` is 0-based.
  const rosterQuery = useRoster(orgId ?? null, cursor.year, cursor.month + 1);
  // First day of the shown month (yyyy-MM-dd), used to seed the add-block panel.
  const monthStart = `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-01`;

  /** Flatten the month's groups into a resourceId → driver lookup for the menu/panel. */
  const driversById = useMemo(() => {
    const map = new Map<string, RosterDriverDto>();
    for (const g of rosterQuery.data?.groups ?? []) {
      for (const d of g.drivers) map.set(d.resourceId, d);
    }
    return map;
  }, [rosterQuery.data]);

  const blockDriver = addBlock ? (driversById.get(addBlock.driverId) ?? null) : null;
  const menuDriver = menu ? (driversById.get(menu.driverId) ?? null) : null;
  // Status of the clicked day (for context-aware menu options); undefined on a row click.
  const menuDayStatus =
    menu?.day != null && menuDriver ? menuDriver.days[menu.day - 1] : undefined;

  /** Build a yyyy-MM-dd for a 1-based day in the shown month. */
  const dateForDay = (day: number) =>
    `${cursor.year}-${String(cursor.month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;

  const openBlock = (mode: BlockMode) => {
    if (menu) {
      // Seed From/To with the clicked day; fall back to the 1st for a row click.
      const date = menu.day ? dateForDay(menu.day) : monthStart;
      setAddBlock({ driverId: menu.driverId, mode, date });
    }
    setMenu(null);
  };

  const openEdit = (block: DriverRosterBlockDto) => {
    setAddBlock({
      driverId: block.driverResourceId,
      mode: block.kind === 'Working' ? 'working' : 'leave',
      date: block.fromDate,
      block,
    });
    setMenu(null);
  };

  return (
    <div className="flex h-full flex-col px-2">
      <PageHeader
        title="Driver Roster"
        description="Plan driver working days, leave and shift patterns."
      />

      {/* Tabs + (roster) month nav & generate. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-slate-200">
        <nav className="flex items-center gap-1">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={cn(
                '-mb-px border-b-2 px-2 py-2 text-sm font-medium transition-colors',
                t.id === tab
                  ? 'border-blue-600 text-blue-700'
                  : 'border-transparent text-slate-500 hover:text-slate-700',
              )}
            >
              {t.label}
            </button>
          ))}
        </nav>

        {tab === 'roster' && (
          <div className="ml-auto flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepMonth(-1)}
                className="rounded-md border border-slate-300 p-1 text-slate-500 hover:bg-slate-50"
                aria-label="Previous month"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="w-28 text-center text-sm font-medium text-slate-700">
                {month.label}
              </span>
              <button
                type="button"
                onClick={() => stepMonth(1)}
                className="rounded-md border border-slate-300 p-1 text-slate-500 hover:bg-slate-50"
                aria-label="Next month"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
            <button
              type="button"
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              Generate rosters
            </button>
          </div>
        )}
      </div>

      {/* Tab body */}
      {tab === 'roster' && (
        <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-hidden pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2 px-1 text-[11px]">
            <span className="text-slate-400">
              Right-click a row for options · Drag cells to add a working day block
            </span>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-slate-500">
              {LEGEND.map((l) => (
                <span key={l.code} className="inline-flex items-center gap-1">
                  <span className={cn('inline-block h-3 w-3 rounded-sm', STATUS[l.code].dot)} />
                  {l.label}
                </span>
              ))}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 gap-1 overflow-hidden">
            <RosterGrid
              month={month}
              groups={rosterQuery.data?.groups ?? []}
              isLoading={rosterQuery.isLoading}
              isError={rosterQuery.isError}
              onRowContextMenu={(driverId, x, y, day) => setMenu({ driverId, x, y, day })}
            />
            {addBlock && blockDriver && orgId && (
              <AddBlockPanel
                mode={addBlock.mode}
                organisationId={orgId}
                driver={blockDriver}
                defaultDate={addBlock.date}
                block={addBlock.block}
                onClose={() => setAddBlock(null)}
              />
            )}
          </div>
        </div>
      )}

      {tab === 'generate' && (
        <Stub
          title="Generate rosters"
          body="Auto-build the month from each driver's pattern, honouring leave and casual blocks. Coming soon."
        />
      )}

      {tab === 'drivers' && (
        <div className="min-h-0 flex-1 overflow-auto px-1 pb-4 pt-3">
          <DriversGrid organisationId={orgId ?? null} />
        </div>
      )}

      {/* Right-click context menu */}
      {menu && menuDriver && orgId && (
        <RosterContextMenu
          organisationId={orgId}
          driverResourceId={menu.driverId}
          driverName={menuDriver.name}
          year={cursor.year}
          month={cursor.month + 1}
          x={menu.x}
          y={menu.y}
          dayStatus={menuDayStatus}
          onAddWorking={() => openBlock('working')}
          onAddLeave={() => openBlock('leave')}
          onEditBlock={openEdit}
          onClose={() => setMenu(null)}
        />
      )}
    </div>
  );
}

function Stub({ title, body }: { title: string; body: string }) {
  return (
    <div className="flex min-h-0 flex-1 items-center justify-center">
      <div className="max-w-sm rounded-md border border-dashed border-slate-300 bg-white px-8 py-12 text-center">
        <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
        <p className="mt-1 text-sm text-slate-500">{body}</p>
      </div>
    </div>
  );
}
